import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { getMyOrders } from "../services/api";
import Seo from "../components/Seo";
import { LogOut, Package, User, Shield } from "lucide-react";

const statusColor = { paid: "bg-green-100 text-green-800", pending_payment: "bg-amber-100 text-amber-800", payment_failed: "bg-red-100 text-red-800", received: "bg-blue-100 text-blue-800" };

export default function Account() {
  const { t, lang } = useApp();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (user === null) navigate("/login");
    if (user) getMyOrders().then(setOrders).catch(() => {});
  }, [user, navigate]);

  if (!user) return <div className="pt-40 text-center text-ink/50">…</div>;

  const statusLabel = (s) => t(`auth.status_${s}`) !== `auth.status_${s}` ? t(`auth.status_${s}`) : s;

  return (
    <div className="pt-20 min-h-screen bg-cream">
      <Seo title={t("auth.account")} description={t("auth.account")} path="/account" lang={lang} noindex />
      <div className="max-w-4xl mx-auto px-6 py-16" data-testid="account-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-wine flex items-center justify-center"><User className="w-6 h-6 text-gold" /></div>
            <div>
              <h1 className="font-serif text-3xl text-ink" data-testid="account-name">{user.name}</h1>
              <p className="text-ink/60 text-sm" data-testid="account-email">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user.role === "admin" && (
              <Link to="/admin"><Button data-testid="admin-area-link" className="bg-wine hover:bg-wine-dark text-white gap-2"><Shield className="w-4 h-4" /> Admin-Bereich</Button></Link>
            )}
            <Button data-testid="logout-button" variant="outline" onClick={async () => { await logout(); navigate("/"); }} className="gap-2 border-wine/30 text-wine hover:bg-wine hover:text-white">
              <LogOut className="w-4 h-4" /> {t("auth.logout")}
            </Button>
          </div>
        </div>

        <h2 className="font-serif text-2xl text-wine mb-5 flex items-center gap-2"><Package className="w-5 h-5" /> {t("auth.myOrders")}</h2>
        {orders.length === 0 ? (
          <div className="bg-white rounded-2xl border border-border p-10 text-center text-ink/50" data-testid="orders-empty">
            <p>{t("auth.noOrders")}</p>
            <Link to="/store"><Button className="mt-4 bg-wine hover:bg-wine-dark text-white">{t("common.continueShopping")}</Button></Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="orders-list">
            {orders.map((o) => (
              <div key={o.id} className="bg-white rounded-2xl border border-border p-5" data-testid="order-card">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <span className="text-xs text-ink/40 font-mono">#{o.id.slice(0, 8)}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-ink/50">{new Date(o.created_at).toLocaleDateString(lang === "de" ? "de-CH" : "en-GB")}</span>
                    <Badge className={`${statusColor[o.status] || "bg-gray-100 text-gray-700"} border-0`}>{statusLabel(o.status)}</Badge>
                  </div>
                </div>
                <ul className="text-sm text-ink/70 space-y-1">
                  {(o.items || []).map((it, i) => (
                    <li key={i} className="flex justify-between"><span>{it.qty}× {it.name}</span><span>CHF {(it.price * it.qty).toFixed(2)}</span></li>
                  ))}
                </ul>
                <div className="border-t border-border mt-3 pt-2 flex justify-between font-semibold text-wine">
                  <span>{t("common.total")}</span><span>CHF {o.total.toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
